'use client'

import { useEffect, useRef, useState } from 'react'

import styles from './GroupStylePanel.module.css'

export type GroupStylePanelProps = {
  variant: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary' | 'senary' | 'septenary'
  content: React.ReactNode
}

export const PhnxGroupStylePanel: React.FC<GroupStylePanelProps> = ({ variant, content }) => {
  const [hasMounted, setHasMounted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const [isWideDesktop, setIsWideDesktop] = useState(false)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setHasMounted(true)
    const mql = window.matchMedia('(min-width: 600px)')
    const wideMql = window.matchMedia('(min-width: 1500px)')

    const updateQueries = () => {
      setIsDesktop(mql.matches)
      setIsWideDesktop(wideMql.matches)
    }

    updateQueries()

    mql.addEventListener('change', updateQueries)
    wideMql.addEventListener('change', updateQueries)

    return () => {
      mql.removeEventListener('change', updateQueries)
      wideMql.removeEventListener('change', updateQueries)
    }
  }, [])

  useEffect(() => {
    if (!hasMounted) return

    const canvas = canvasRef.current
    const container = containerRef.current

    if (canvas && container) {
      const context = canvas.getContext('2d')
      if (!context) return

      const col = (x: number, y: number, r: number, g: number, b: number) => {
        context.fillStyle = `rgb(${r}, ${g}, ${b})`
        context.fillRect(x, y, 1, 1)
      }

      const R = (x: number, y: number, t: number) =>
        Math.floor(192 + 64 * Math.cos((x * x - y * y) / 300 + t))
      const G = (x: number, y: number, t: number) =>
        Math.floor(192 + 64 * Math.sin((x * x * Math.cos(t / 4) + y * y * Math.sin(t / 3)) / 300))
      const B = (x: number, y: number, t: number) =>
        Math.floor(
          192 + 64 * Math.sin(5 * Math.sin(t / 9) + ((x - 100) ** 2 + (y - 100) ** 2) / 1100)
        )

      let t = 0
      const run = () => {
        for (let x = 0; x <= 35; x++) {
          for (let y = 0; y <= 35; y++) {
            col(x, y, R(x, y, t), G(x, y, t), B(x, y, t))
          }
        }
        t += 0.05
        window.requestAnimationFrame(run)
      }

      run()
    }
  }, [hasMounted])

  if (!hasMounted) return null

  return (
    <div
      className={`${styles['groupstylepanel']} ${styles[`variant-${variant}`]}`}
      ref={containerRef}
    >
      {variant === 'secondary' && isDesktop && (
        <div className={styles['animation']}>
          <canvas id="canv" width="32" height="32" ref={canvasRef}></canvas>
          <img src="/assets/mask_grouppanel_xwide8.webp" className={styles['featuremask']} alt="" />
        </div>
      )}
      {variant === 'secondary' && !isDesktop && !isWideDesktop && (
        <>
          <canvas id="canv" width="32" height="32" ref={canvasRef}></canvas>
          <img
            src="/assets/mask_grouppanel_mobile.webp"
            className={styles['featuremaskmobile']}
            alt=""
          />
        </>
      )}
      {content}
    </div>
  )
}
